import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';
import { api } from '../../services/apiClient';

/**
 * SocialAuthButton — single pill used three times on the Sign-in page:
 * Continue with Google · Continue with GitHub · Continue with SSO.
 *
 * Google is live: clicking the Google pill performs a full-page navigation to
 * `${API_BASE}/api/v1/auth/google`, which 302s through Google's consent screen
 * and back to our SPA's /auth/google/done route. We use a hard redirect (not
 * XHR + window.open) because Google requires a top-level browser navigation
 * for the consent screen — popups get killed by modern browsers' SameSite
 * cookie rules.
 *
 * GitHub + SSO remain stubs: POST → 501 → "coming soon" inline toast.
 *
 * The icons are inline SVG (brand marks for Google + GitHub) so we don't add a
 * new dependency; SSO uses Lucide's Building2 since "SSO" has no brand mark.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:15001';

export type SocialProvider = 'google' | 'github' | 'microsoft' | 'sso';

export function SocialAuthButton({
  provider,
  onMessage,
}: {
  provider: SocialProvider;
  /** Parent supplies a toast sink so all three pills share the same toast slot. */
  onMessage?: (msg: string) => void;
}) {
  const { t } = useTranslation('auth');
  const [busy, setBusy] = useState(false);

  // Provider display names are proper nouns — identical value in every
  // locale (see socialAuthButton.providerNames in auth.json).
  const providerName = t(`socialAuthButton.providerNames.${provider}`);
  const label = t('socialAuthButton.continueWith', { provider: providerName });

  async function go() {
    // Google + Microsoft — real OAuth flow. Top-level browser navigation: the
    // backend 302s to the provider's consent screen, which 302s back to
    // /auth/<provider>/done with a JWT (success) or error code (failure) in
    // the query string. We don't toggle `busy` because the page unloads
    // immediately.
    if (provider === 'google' || provider === 'microsoft') {
      window.location.href = `${API_BASE}/api/v1/auth/${provider}`;
      return;
    }

    // GitHub + SSO are still stubs — POST + surface server message as toast.
    setBusy(true);
    try {
      await api.post(`/api/v1/auth/${provider}`);
      // If we ever ship the real flow this branch will redirect.
    } catch (e: any) {
      const status = e?.response?.status;
      const msg =
        e?.response?.data?.message ||
        (status === 501
          ? t('socialAuthButton.comingSoon', { provider: providerName })
          : t('socialAuthButton.startFailed', { provider: providerName }));
      onMessage?.(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={go}
      disabled={busy}
      className="oauth-btn"
      aria-label={label}
    >
      <ProviderIcon provider={provider} />
      <span>{busy ? t('socialAuthButton.opening') : label}</span>
    </button>
  );
}

function ProviderIcon({ provider }: { provider: SocialProvider }) {
  if (provider === 'google') {
    // Google "G" mark — official 4-color logo, simplified for monochrome contexts.
    return (
      <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8a12 12 0 1 1 7.9-21l5.7-5.7A20 20 0 1 0 44 24c0-1.3-.1-2.4-.4-3.5z"/>
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8A12 12 0 0 1 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7A20 20 0 0 0 6.3 14.7z"/>
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2A12 12 0 0 1 12.7 28l-6.5 5A20 20 0 0 0 24 44z"/>
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4.1 5.6l6.2 5.2c-.4.4 6.6-4.8 6.6-14.8 0-1.3-.1-2.4-.4-3.5z"/>
      </svg>
    );
  }
  if (provider === 'github') {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.39 7.86 10.91.58.11.79-.25.79-.55v-2.07c-3.2.7-3.87-1.36-3.87-1.36-.52-1.33-1.28-1.69-1.28-1.69-1.05-.71.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.03 1.76 2.7 1.25 3.36.96.1-.75.4-1.26.74-1.55-2.55-.29-5.24-1.28-5.24-5.69 0-1.26.45-2.29 1.19-3.1-.12-.29-.52-1.47.11-3.07 0 0 .97-.31 3.18 1.19a11 11 0 0 1 5.8 0c2.2-1.5 3.17-1.19 3.17-1.19.63 1.6.24 2.78.12 3.07.74.81 1.18 1.84 1.18 3.1 0 4.42-2.69 5.39-5.26 5.68.41.36.78 1.06.78 2.14v3.17c0 .31.21.67.79.55A11.5 11.5 0 0 0 23.5 12C23.5 5.65 18.35.5 12 .5z"/>
      </svg>
    );
  }
  if (provider === 'microsoft') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#00A4EF" d="M1 1h10v10H1z"/><path fill="#FFB900" d="M13 1h10v10H13z"/>
        <path fill="#F25022" d="M1 13h10v10H1z"/><path fill="#7FBA00" d="M13 13h10v10H13z"/>
      </svg>
    );
  }
  // SSO — no brand mark; use a generic building/enterprise glyph.
  return <Building2 size={16} strokeWidth={1.75} aria-hidden="true" />;
}
